import React from "react";
import moment from "moment";
import {
  Chip, CircularProgress, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Copy01Icon, Delete02Icon, EyeIcon, EyeOffIcon, PencilEdit01Icon} from "@hugeicons/core-free-icons";

const COLUMNS = ["Key", "Owner", "Organization", "Created", "Expires", "Action"];

const formatDate = (date) => date ? moment(date).format("DD MMM YYYY, HH:mm") : "—";

export const ApiKeysTable = ({
  apiKeys, loading, emptyText, revealedKeys, onToggleReveal, onCopy, onEdit, onDelete, size, hideOrganization
}) => {
  const columns = hideOrganization ? COLUMNS.filter(column => column !== "Organization") : COLUMNS;

  return (
    <Table size={size}>
      <TableHead>
        <TableRow>
          {columns.map(column => (
            <TableCell key={column} sx={{fontWeight: 700}}>{column}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {loading && (
          <TableRow>
            <TableCell colSpan={columns.length} align={"center"} sx={{py: 4}}>
              <CircularProgress size={28}/>
            </TableCell>
          </TableRow>
        )}
        {!loading && apiKeys.length === 0 && (
          <TableRow>
            <TableCell colSpan={columns.length} align={"center"} sx={{py: 4, color: "text.secondary"}}>
              {emptyText}
            </TableCell>
          </TableRow>
        )}
        {!loading && apiKeys.map(apiKey => (
          <ApiKeyRow
            key={apiKey.id}
            apiKey={apiKey}
            revealedKey={revealedKeys[apiKey.id]}
            hideOrganization={hideOrganization}
            onToggleReveal={onToggleReveal}
            onCopy={onCopy}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </TableBody>
    </Table>
  );
};

const ApiKeyRow = ({apiKey, revealedKey, hideOrganization, onToggleReveal, onCopy, onEdit, onDelete}) => {
  const key = revealedKey || apiKey.maskedKey;

  return (
    <TableRow>
      <TableCell>
        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
          <Tooltip title={key}>
            <Typography
              variant={"body2"} fontFamily={"monospace"} fontSize={13} noWrap
              sx={{maxWidth: 240, userSelect: revealedKey ? "all" : "none"}}
            >
              {key}
            </Typography>
          </Tooltip>
          <Tooltip title={revealedKey ? "Hide" : "Reveal"}>
            <IconButton size={"small"} sx={{borderRadius: "50% !important"}} onClick={() => onToggleReveal(apiKey)}>
              <HugeiconsIcon icon={revealedKey ? EyeOffIcon : EyeIcon} size={16}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Copy"}>
            <IconButton size={"small"} sx={{borderRadius: "50% !important"}} onClick={() => onCopy(apiKey)}>
              <HugeiconsIcon icon={Copy01Icon} size={16}/>
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
      <TableCell>{apiKey.username}</TableCell>
      {!hideOrganization && (
        <TableCell>
          <Typography variant={"body2"} color={apiKey.organizationName ? "text.primary" : "text.secondary"}>
            {apiKey.organizationName || "Personal"}
          </Typography>
        </TableCell>
      )}
      <TableCell>
        <Typography variant={"body2"} noWrap>{formatDate(apiKey.dateCreated)}</Typography>
      </TableCell>
      <TableCell>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <Typography variant={"body2"} noWrap>{formatDate(apiKey.validUntil)}</Typography>
          {apiKey.expired && <Chip label={"Expired"} size={"small"} color={"error"} variant={"outlined"}/>}
        </Stack>
      </TableCell>
      <TableCell padding={"checkbox"}>
        <Stack direction={"row"} gap={1} alignItems={"center"} sx={{px: 1}}>
          <Tooltip title={"Change expiry"}>
            <IconButton sx={{borderRadius: "50% !important"}} onClick={() => onEdit(apiKey)}>
              <HugeiconsIcon icon={PencilEdit01Icon} size={18}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Revoke"}>
            <IconButton color={"error"} sx={{borderRadius: "50% !important"}} onClick={() => onDelete(apiKey)}>
              <HugeiconsIcon icon={Delete02Icon} size={18}/>
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
};
